import React from 'react'

import styled from '@emotion/styled'

import { AlertFillIcon, ArcadeIcon } from '@arcade/web/icons/react'

import spacing from '../../theme/spacing'
import { Flex } from '../layout/Flex'

/** Error Icon shown in form field when invalid */
export const ErrorIconContainer = styled(Flex)<{ isTextArea?: boolean }>`
  position: absolute;
  transform: translateY(-50%);
  right: ${spacing.m};
  top: ${({ isTextArea }) => (isTextArea ? '1.5rem' : '50%')};
`

export type GetErrorIconProps = {
  isInvalid: boolean
  shouldShowErrorIcon: boolean
  disabled: boolean
  isTextArea?: boolean
}

export const getErrorIcon = ({ isInvalid, shouldShowErrorIcon, disabled, isTextArea = false }: GetErrorIconProps) => {
  if (!isInvalid || !shouldShowErrorIcon || disabled) {
    return null
  }

  return (
    <ErrorIconContainer alignItems="center" justifyContent="center" isTextArea={isTextArea}>
      <AlertFillIcon size={16} variant="danger" data-testid="arcade-input-trailing-error-icon" />
    </ErrorIconContainer>
  )
}

/** Help, error, and disabled text component */
export type ArcadeHelpTextProps = {
  helpTextId?: string
  helpText?: string
  HelpTextIcon?: ArcadeIcon
  isInvalid?: boolean
  errorText?: string
  disabled?: boolean
  disabledText?: string
  marginTop?: keyof typeof spacing
}

/**
 * Reusable props for components that use ArcadeHelpText
 * Excludes helpTextId since that's internally managed by each component
 */
export type ArcadeHelpTextComponentProps = Omit<ArcadeHelpTextProps, 'helpTextId'>

/**
 * Styles for help text, error text, reason for being disabled
 */
export const HelpTextContainer = styled(Flex)<{ marginTop?: keyof typeof spacing }>`
  margin-top: ${({ marginTop }) => (marginTop ? spacing[marginTop] : spacing.xs)};
`

const createStyledHelpTextIcon = (HelpTextIcon: ArcadeIcon) =>
  styled(HelpTextIcon)`
    margin-right: ${spacing.xxs};
  `

export const HelpText = styled.span<{ isInvalid?: boolean; disabled?: boolean }>`
  ${({ theme }) => theme.typography.helpText}

  ${({ isInvalid, theme }) => isInvalid && `color: ${theme.arcadeColors.component.inputHelperTextError};`}
  ${({ disabled, theme }) => disabled && `color: ${theme.arcadeColors.component.inputTextDisabled};`}
`

/**
 * If input is disabled, display disabled text. If input is invalid, display error text, if provided.
 * If input neither disabled or invalid, display help text & help text icon, or only help text.
 */

const ArcadeHelpText: React.FC<ArcadeHelpTextProps> = ({
  helpTextId,
  helpText,
  HelpTextIcon,
  isInvalid,
  errorText,
  disabled,
  disabledText,
  marginTop,
}) => {
  const hasTextContent = helpText || errorText || disabledText

  if (!hasTextContent) {
    return null
  }

  const StyledHelpTextIcon = HelpTextIcon ? createStyledHelpTextIcon(HelpTextIcon) : null

  const textContent =
    disabled && disabledText ? (
      <HelpText id={helpTextId} disabled={true}>
        {disabledText}
      </HelpText>
    ) : isInvalid && errorText && !disabled ? (
      <HelpText id={helpTextId} isInvalid={true}>
        {errorText}
      </HelpText>
    ) : helpText && StyledHelpTextIcon ? (
      <>
        <StyledHelpTextIcon
          size={16}
          variant={disabled ? 'disabled' : 'subtle'}
          data-testid="arcade-input-help-text-icon"
        />
        <HelpText id={helpTextId}>{helpText}</HelpText>
      </>
    ) : helpText ? (
      <HelpText id={helpTextId}>{helpText}</HelpText>
    ) : null

  return textContent ? (
    <HelpTextContainer alignItems="center" flexDirection="row" marginTop={marginTop}>
      {textContent}
    </HelpTextContainer>
  ) : null
}

/**
 * Utility function to combine existing aria-describedby with helpTextId
 * This ensures that both component-specific descriptions and help text are properly associated
 * with form controls for accessibility.
 *
 * @param existingAriaDescribedBy - Any existing aria-describedby value from props
 * @param helpTextId - The ID of the help text element
 * @returns Combined aria-describedby value or undefined if no descriptions exist
 */
export const combineAriaDescribedBy = (existingAriaDescribedBy?: string, helpTextId?: string): string | undefined => {
  const combinedAriaDescribedBy = [existingAriaDescribedBy, helpTextId]
    .filter(Boolean) // Remove undefined/null values
    .join(' ') // Join with space
    .trim() // Remove leading and trailing whitespace

  return combinedAriaDescribedBy || undefined
}

export default ArcadeHelpText
